import React, { useState, useEffect } from 'react';
import {
    Box, Button, CircularProgress, CssBaseline, Divider,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
    IconButton, InputAdornment, Link, TextField, Typography,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    signInWithEmailAndPassword,
    GoogleAuthProvider,
    getMultiFactorResolver,
    TotpMultiFactorGenerator,
} from 'firebase/auth';
import { firebaseAuth, getErrorMessage } from '../../firebase';
import { useHistory } from 'react-router-dom';
import { getBrand } from '../../branding';

// Stile Web Board: sfondo grigio, tutto dentro una card bianca.
const PAGE_BG = '#f4f5f7';
const BORDER = '#e5e7eb';
const fieldSx = brand => ({
    '& .MuiOutlinedInput-root': { borderRadius: '10px' },
    '& .MuiOutlinedInput-root.Mui-focused fieldset': { borderColor: brand.color },
    '& :-webkit-autofill': { transitionDelay: '9999s' },
});

function FieldLabel({ children, sx }) {
    return <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75, ...sx }}>{children}</Typography>;
}

/** Logo del marchio, cliccabile verso il sito se il marchio ne ha uno. */
export function BrandLogo({ brand, height }) {
    const img = <Box component="img" src={brand.logo} alt={brand.name} sx={{ height, maxWidth: '100%', display: 'block' }} />;
    return brand.homeUrl
        ? <a href={brand.homeUrl} target="_blank" rel="noopener noreferrer">{img}</a>
        : img;
}

const isInAppBrowser = () => {
    const ua = navigator.userAgent || '';
    return /FBAN|FBAV|Instagram|Telegram|Line|MicroMessenger/i.test(ua) || /; wv\)/.test(ua);
};


const GOOGLE_ICON = (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
        <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
        <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
        <path d="M3.964 10.71A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
        <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
);

export function LoginPage({ onSubmit, onResetPassword, error: externalError }) {
    const { t } = useTranslation();
    const history = useHistory();
    const brand = getBrand();
    const brandButtonSx = {
        py: 1.25, borderRadius: '10px', textTransform: 'none', fontSize: '1rem', fontWeight: 600,
        bgcolor: brand.color, color: brand.contrastText, '&:hover': { bgcolor: brand.color, filter: 'brightness(0.92)' },
    };

    const [tab, setTab] = useState('email');
    const [code, setCode] = useState('');
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loginError, setLoginError] = useState('');
    const [loadingEmail, setLoadingEmail] = useState(false);
    const [loadingGoogle, setLoadingGoogle] = useState(false);

    const [forgotPassword, setForgotPassword] = useState(false);

    // Stato 2FA
    const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
    const [mfaOtp, setMfaOtp] = useState('');
    const [mfaResolver, setMfaResolver] = useState(null);
    const [mfaError, setMfaError] = useState('');

    // ─── Gestione risultato redirect Google (mobile) ──────────────────────
    useEffect(() => {
        getRedirectResult(firebaseAuth).catch(e => {
            if (e.code === 'auth/multi-factor-auth-required') {
                const resolver = getMultiFactorResolver(firebaseAuth, e);
                setMfaResolver(resolver);
                setMfaDialogOpen(true);
            } else {
                const msg = getErrorMessage(e);
                if (msg) setLoginError(msg);
            }
        });
    }, []);

    // ─── Login email/password con gestione MFA ────────────────────────────
    async function handleEmailSubmit() {
        setLoginError('');
        setLoadingEmail(true);
        try {
            await signInWithEmailAndPassword(firebaseAuth, username, password);
            // onAuthStateChanged in App.js gestisce il redirect
        } catch (e) {
            if (e.code === 'auth/multi-factor-auth-required') {
                const resolver = getMultiFactorResolver(firebaseAuth, e);
                setMfaResolver(resolver);
                setMfaDialogOpen(true);
            } else {
                onSubmit(username, password);
            }
        }
        setLoadingEmail(false);
    }

    // ─── Login con Google ─────────────────────────────────────────────────
    async function handleGoogleSignIn() {
        if (isInAppBrowser()) {
            setLoginError(t('openInBrowser'));
            return;
        }
        setLoginError('');
        setLoadingGoogle(true);
        const provider = new GoogleAuthProvider();
        try {
            await signInWithPopup(firebaseAuth, provider);
            // onAuthStateChanged in App.js gestisce il redirect
        } catch (e) {
            if (e.code === 'auth/multi-factor-auth-required') {
                const resolver = getMultiFactorResolver(firebaseAuth, e);
                setMfaResolver(resolver);
                setMfaDialogOpen(true);
            } else if (['auth/popup-blocked', 'auth/popup-closed-by-user', 'auth/cancelled-popup-request'].includes(e.code)) {
                // Popup non disponibile → fallback redirect
                await signInWithRedirect(firebaseAuth, provider);
            } else {
                const msg = getErrorMessage(e);
                if (msg) setLoginError(msg);
            }
        }
        setLoadingGoogle(false);
    }

    // ─── Conferma codice TOTP 2FA ─────────────────────────────────────────
    async function confirmMfaLogin() {
        setMfaError('');
        if (!mfaOtp) { setMfaError(t('mandatoryField')); return; }
        try {
            const hint = mfaResolver.hints[0];
            const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, mfaOtp);
            await mfaResolver.resolveSignIn(assertion);
            setMfaDialogOpen(false);
            setMfaOtp('');
        } catch (e) {
            console.error(e);
            setMfaError(t('wrongOtp'));
        }
    }

    const onKeyDown = (ev) => {
        if (ev.key === 'Enter') handleEmailSubmit();
    };

    const resetPassword = () => {
        setForgotPassword(false);
        onResetPassword(username);
    };

    const displayError = loginError || externalError;
    const busy = loadingEmail || loadingGoogle;
    const cleanCode = code.replace(/\s+/g, '');
    const openCode = () => { if (cleanCode) history.push(`/monitor/${encodeURIComponent(cleanCode)}`); };

    return (
        <Box component="main" sx={{
            minHeight: '100vh', bgcolor: PAGE_BG, display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'center', px: 2, py: { xs: 3, sm: 6 },
        }}>
            <CssBaseline />
            <Box sx={{
                width: '100%', maxWidth: 480, bgcolor: '#fff', border: `1px solid ${BORDER}`, borderRadius: '16px',
                px: { xs: 3, sm: 5 }, py: { xs: 4, sm: 5 },
            }}>
                {/* Marchio */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                    <BrandLogo brand={brand} height={brand.logoHeight.login} />
                </Box>

                <Typography component="h1" variant="h5" align="center" sx={{ fontWeight: 700 }}>
                    {t('doLogIn')}
                </Typography>
                <Typography variant="body2" align="center" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
                    {t('loginSubtitle')}
                </Typography>

                {/* Schede: email e password / codice di accesso */}
                <Box sx={{ display: 'flex', bgcolor: '#f3f4f6', borderRadius: '10px', p: 0.5, mb: 3 }}>
                    {[['email', t('loginTabEmail')], ['code', t('loginTabCode')]].map(([key, label]) => (
                        <Button key={key} fullWidth disableElevation onClick={() => setTab(key)}
                            sx={{
                                textTransform: 'none', fontWeight: 600, borderRadius: '8px', py: 1,
                                color: tab === key ? 'text.primary' : 'text.secondary',
                                bgcolor: tab === key ? '#fff' : 'transparent',
                                boxShadow: tab === key ? '0 1px 3px rgba(0,0,0,.12)' : 'none',
                                '&:hover': { bgcolor: tab === key ? '#fff' : 'rgba(0,0,0,.04)' },
                            }}>
                            {label}
                        </Button>
                    ))}
                </Box>

                {tab === 'email' ? <>
                    {/* Google SSO */}
                    <Button
                        fullWidth variant="outlined" size="large"
                        onClick={handleGoogleSignIn}
                        disabled={busy}
                        startIcon={loadingGoogle ? null : GOOGLE_ICON}
                        sx={{ py: 1.25, borderRadius: '10px', textTransform: 'none', fontSize: '1rem', color: 'text.primary', borderColor: BORDER }}
                    >
                        {loadingGoogle
                            ? <CircularProgress size={22} />
                            : t('continueWithGoogle')}
                    </Button>

                    <Divider sx={{ my: 2.5 }}>
                        <Typography variant="body2" color="text.secondary">{t('or')}</Typography>
                    </Divider>

                    {/* Email */}
                    <FieldLabel>{t('emailAddress')}</FieldLabel>
                    <TextField
                        fullWidth variant="outlined" size="small" type="email"
                        autoComplete="email" autoFocus
                        value={username}
                        onChange={e => setUsername(e.target.value)}
                        onKeyDown={onKeyDown}
                        sx={fieldSx(brand)}
                    />

                    {/* Password */}
                    <FieldLabel sx={{ mt: 2 }}>{t('password')}</FieldLabel>
                    <TextField
                        fullWidth variant="outlined" size="small"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        value={password}
                        onChange={e => setPassword(e.target.value)}
                        onKeyDown={onKeyDown}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(p => !p)}
                                        onMouseDown={e => e.preventDefault()}
                                        edge="end" size="small"
                                    >
                                        {showPassword ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={fieldSx(brand)}
                    />
                    <Box sx={{ textAlign: 'right', mt: 1 }}>
                        <Link component="button" variant="body2" color="text.secondary"
                            onClick={() => setForgotPassword(true)} underline="hover">
                            {t('forgotPassword')}
                        </Link>
                    </Box>

                    {displayError && (
                        <Typography color="error" variant="body2" align="center" sx={{ mt: 1 }}>
                            {displayError}
                        </Typography>
                    )}

                    <Button
                        fullWidth variant="contained" size="large" disableElevation
                        onClick={handleEmailSubmit}
                        disabled={busy}
                        sx={{ ...brandButtonSx, mt: 2.5 }}
                    >
                        {loadingEmail ? <CircularProgress size={22} color="inherit" /> : t('join')}
                    </Button>

                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2.5 }}>
                        {t('noAccount')}{' '}
                        <Link href="/newPinbiker" underline="hover" sx={{ color: brand.color, fontWeight: 600 }}>
                            {t('registerHere')}
                        </Link>
                    </Typography>
                </> : <>
                    {/* Codice del link condivisibile del pedale */}
                    <Box sx={{ bgcolor: '#f7f7f8', borderLeft: `3px solid ${BORDER}`, borderRadius: '8px', px: 2, py: 1.5, mb: 2.5 }}>
                        <Typography variant="body2" color="text.secondary">{t('loginCodeInfo')}</Typography>
                    </Box>
                    <FieldLabel>{t('pedalShareEnterCode')}</FieldLabel>
                    <TextField
                        fullWidth variant="outlined" size="small" autoFocus
                        value={code}
                        onChange={e => setCode(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') openCode(); }}
                        inputProps={{ autoComplete: 'off', spellCheck: false, style: { fontFamily: 'monospace' } }}
                        sx={fieldSx(brand)}
                    />
                    <Button
                        fullWidth variant="contained" size="large" disableElevation
                        onClick={openCode} disabled={!cleanCode}
                        sx={{ ...brandButtonSx, mt: 2.5 }}
                    >
                        {t('pedalShareOpen')}
                    </Button>
                </>}

                <Typography variant="caption" component="div" align="center" sx={{ mt: 4, color: '#9ca3af' }}>
                    © {new Date().getFullYear()} {brand.owner}
                </Typography>
            </Box>

            {/* Dialog recupero password */}
            <Dialog open={forgotPassword} onClose={() => setForgotPassword(false)}>
                <DialogTitle>{t('passwordRecovery')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {username
                            ? t('forgotPasswordConfirm').replace('${username}', username)
                            : t('forgotPasswordError')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setForgotPassword(false)} color="secondary">
                        {t('abort')}
                    </Button>
                    {username && (
                        <Button onClick={resetPassword} color="primary" autoFocus>
                            {t('send')}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Dialog OTP 2FA */}
            <Dialog
                open={mfaDialogOpen}
                onClose={() => { setMfaDialogOpen(false); setMfaOtp(''); setMfaError(''); }}
            >
                <DialogTitle>{t('twoFactorAuth')}</DialogTitle>
                <DialogContent>
                    <DialogContentText>{t('insertOtp')}</DialogContentText>
                    <TextField
                        autoFocus fullWidth margin="dense"
                        label={t('otpCode')}
                        value={mfaOtp}
                        onChange={e => { setMfaOtp(e.target.value); setMfaError(''); }}
                        onKeyDown={e => { if (e.key === 'Enter') confirmMfaLogin(); }}
                        error={!!mfaError} helperText={mfaError}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => { setMfaDialogOpen(false); setMfaOtp(''); setMfaError(''); }}
                        color="secondary"
                    >
                        {t('abort')}
                    </Button>
                    <Button onClick={confirmMfaLogin} color="primary">
                        {t('verify')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export function LogoutView({ onClick }) {
    return (
        <div>
            <span>You are logged in</span>
            <button onClick={onClick}>Logout</button>
        </div>
    );
}
